import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/auth/AuthContext';
import { useUsers, useCompanies, useStartImpersonation } from '@/lib/adminApi';
import type { UserAdminSummary } from '@ventin/shared';

/**
 * ImpersonationPage — /admin/impersonation (ADM-02).
 *
 * Lists non-ADMIN users (CLIENT + SUPPORT) with an Impersonate action.
 * On confirm, calls the start endpoint, which swaps the browser's HttpOnly
 * session cookie to the target user. AuthContext refreshes from /me and the
 * ImpersonationBanner renders automatically.
 *
 * If the current admin is already impersonating (auth.impersonatedBy !=
 * null), the picker is replaced with a "stop first" message so the admin
 * cannot chain impersonations without an explicit stop.
 */
export default function ImpersonationPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const clientsQ = useUsers({ role: 'CLIENT' });
  const supportQ = useUsers({ role: 'SUPPORT' });
  const { data: companiesData } = useCompanies();
  const startMut = useStartImpersonation();

  const [target, setTarget] = useState<UserAdminSummary | null>(null);

  const users = useMemo<UserAdminSummary[]>(() => {
    const combined = [...(clientsQ.data?.users ?? []), ...(supportQ.data?.users ?? [])];
    // Defensive: filter out ADMIN even if the API returns one
    return combined.filter((u) => u.role !== 'ADMIN');
  }, [clientsQ.data, supportQ.data]);

  const companyNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of companiesData?.companies ?? []) m.set(c.id, c.name);
    return m;
  }, [companiesData]);

  const alreadyImpersonating = auth.impersonatedBy != null;
  const isLoading = clientsQ.isLoading || supportQ.isLoading;
  const isError = clientsQ.isError || supportQ.isError;

  async function handleConfirm() {
    if (!target) return;
    try {
      const res = await startMut.mutateAsync({ userId: target.id });
      await auth.refresh();
      setTarget(null);
      toast.success(`Now impersonating ${res.target.email}`);
      navigate('/');
    } catch {
      toast.error("Couldn't start impersonation — try again");
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <header className="mb-6">
        <h2 className="text-2xl font-semibold leading-[1.3]">Impersonation</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Act as another user for support or debugging. Every impersonation session is recorded in
          the audit log.
        </p>
      </header>

      {alreadyImpersonating ? (
        <Alert>
          <AlertDescription>
            You are currently impersonating. Stop the current session to start a new one.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {isLoading && <p className="text-sm text-muted-foreground">Loading users…</p>}
          {isError && (
            <Alert variant="destructive">
              <AlertDescription>Couldn't load users.</AlertDescription>
            </Alert>
          )}
          {!isLoading && !isError && (
            <div className="rounded-md border bg-background overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="px-4 py-3 font-medium">Company</th>
                    <th className="px-4 py-3 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-6 text-center text-sm text-muted-foreground"
                      >
                        No impersonatable users.
                      </td>
                    </tr>
                  )}
                  {users.map((u) => {
                    const companyLabel = u.companyId
                      ? (companyNameById.get(u.companyId) ?? u.companyId)
                      : '—';
                    return (
                      <tr key={u.id} className="border-t">
                        <td className="px-4 py-3 font-medium">{u.fullName}</td>
                        <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                        <td className="px-4 py-3 text-muted-foreground">{u.role}</td>
                        <td className="px-4 py-3 text-muted-foreground">{companyLabel}</td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setTarget(u)}
                            aria-label={`Impersonate ${u.fullName}`}
                          >
                            Impersonate {u.fullName}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Dialog open={target !== null} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm impersonation</DialogTitle>
            <DialogDescription>
              {target
                ? `Impersonate ${target.fullName}? This will switch your browser session to act as them. All actions will be logged.`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTarget(null)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={startMut.isPending}>
              {startMut.isPending ? 'Starting…' : 'Confirm impersonation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
